import useI18n from '@/shared/hooks/usei18n'
import { Button, GenericDialog } from '@/common'
import { useCallback } from 'react'
import { OrganizationUser } from '@/features/organization/hooks/users'
import { useSession } from '@/shared/hooks/useSession'
import { useAssignDeclarantToUser, useUnassignDeclarantFromUser, useGetAllDeclarants } from '@/features/cbam-reporting/hooks/declarants'
import toast from 'react-hot-toast'

function DeclarantRow({
  declarantId,
  declarantName,
  isAssigned,
  userId,
  organizationId
}: {
  declarantId: string
  declarantName: string
  isAssigned: boolean
  userId: string
  organizationId: string
}) {
  const { t } = useI18n()
  const { mutateAsync: assignDeclarant, isPending: isAssigning } = useAssignDeclarantToUser(
    organizationId,
    declarantId
  )
  const { mutateAsync: unassignDeclarant, isPending: isUnassigning } = useUnassignDeclarantFromUser(
    organizationId,
    declarantId,
    userId
  )

  const isPending = isAssigning || isUnassigning

  const handleAssign = useCallback(async () => {
    try {
      await assignDeclarant({ userId })
      toast.success(t('userManagement:editPermissionsDialog.notifications.assignSuccess'))
    } catch (error) {
      console.error(error)
      toast.error(t('userManagement:editPermissionsDialog.notifications.error'))
    }
  }, [assignDeclarant, userId, t])

  const handleUnassign = useCallback(async () => {
    try {
      await unassignDeclarant()
      toast.success(t('userManagement:editPermissionsDialog.notifications.unassignSuccess'))
    } catch (error) {
      console.error(error)
      toast.error(t('userManagement:editPermissionsDialog.notifications.error'))
    }
  }, [unassignDeclarant, t])

  return (
    <div className="flex items-center justify-between py-3 px-4 border-b border-border-default last:border-b-0">
      <span className="text-sm font-medium text-text-primary">{declarantName}</span>
      <div className="flex items-center gap-2">
        {isAssigned ? (
          <Button
            variant="txt-danger"
            sizeType="xs"
            onClick={handleUnassign}
            disabled={isPending}
            isLoading={isUnassigning}
          >
            {t('userManagement:editPermissionsDialog.actions.unassign')}
          </Button>
        ) : (
          <Button
            variant="subtle"
            sizeType="xs"
            onClick={handleAssign}
            disabled={isPending}
            isLoading={isAssigning}
          >
            {t('userManagement:editPermissionsDialog.actions.assign')}
          </Button>
        )}
      </div>
    </div>
  )
}

export default function EditPermissionsDialog({
  isOpen,
  close,
  user
}: {
  isOpen: boolean
  close: () => void
  user: OrganizationUser | null
}) {
  const { t } = useI18n()
  const { session } = useSession()
  const organizationId = session.organization.id
  const { data: declarants = [] } = useGetAllDeclarants(organizationId)

  const onClose = useCallback(() => {
    close()
  }, [close])

  const declarantsList = declarants.map(declarant => ({
    id: declarant.id,
    name: declarant.name,
    type: declarant.type,
    isAssigned: user?.declarants.some(userDeclarant => userDeclarant.declarantId === declarant.id)
  }))

  return (
    <GenericDialog
      isOpen={isOpen}
      close={onClose}
      title={t('userManagement:editPermissionsDialog.title', {
        userName: `${user?.firstName} ${user?.lastName}`
      })}
      size="md"
    >
      <div className="space-y-4">
        <p className="text-sm text-text-secondary">
          {t('userManagement:editPermissionsDialog.description')}
        </p>
        <div className="border border-border-default rounded-xs overflow-hidden">
          {declarants.length === 0 ? (
            <div className="py-8 px-4 text-center text-sm text-text-secondary">
              {t('userManagement:editPermissionsDialog.noDeclarants')}
            </div>
          ) : (
            declarantsList.map(declarant => (
              <DeclarantRow
                key={declarant.id}
                declarantId={declarant.id}
                declarantName={declarant.name}
                isAssigned={declarant.isAssigned || false}
                userId={user?.id || ''}
                organizationId={organizationId}
              />
            ))
          )}
        </div>
        <footer className="flex justify-end gap-2 pt-4">
          <Button type="button" variant="neutral" onClick={onClose}>
            {t('common:actions.close')}
          </Button>
        </footer>
      </div>
    </GenericDialog>
  )
}
